import { defineStore } from 'pinia';
import {
  AdditionalRequests,
  CashSimulationDemand, InferredReplacement,
  LoanVariant,
  MortgageSimulationDemand,
  ReplacementKind, 
  Replacements, 
  SimulationDemand
} from '@/models/Demand';
import { Simulation } from '@/models/simulation/Simulation';
import type { SimulationDTO } from '@/models/simulation/SimulationDTO';
import { FFHousehold } from '@/models/simulation/FFHousehold';
import { Offer } from "@/models/simulation/subtypes";
import { debouncedAtInput, removePropertyFromObject } from '@/services/utils/BasicUtils';
import CashSimulationApi from '@/modules/calculator-v25/services/cash/CashSimulationApi';
import MortgageSimulationApi from '@/modules/calculator-v25/services/mortgage/MortgageSimulationApi';
import type {
  CashCreateSimulationRequest,
  CashDuplicateLoanRequest,
  CashIsLoanAvailableRequest,
  CashRemoveDuplicateRequest,
  CashUpdateSimulationByIdRequest,
} from '@/modules/calculator-v25/types/cash/dto/endpoints';
import type {
  MortgageCreateSimulationRequest,
  MortgageDuplicateLoanRequest,
  MortgageGetHomeStartAssumptionsRequest,
  MortgageIsLoanAvailableRequest,
  MortgageRemoveDuplicateRequest,
  MortgageUpdatePersistentSimulationByIdRequest,
  MortgageUpdateSimulationByIdRequest,
} from '@/modules/calculator-v25/types/mortgage/dto/endpoints';
import { ProductType } from '@/commons/enums/ProductType';
import Vue from 'vue';
import { banksDictionary } from '@/commons/dictionaries/BanksDictionary';
import useInitialDemands from '@/components/UseInitialDemands';
import GoToService from '@/router/GoToService';
import { FormSteps } from '@/components/calculator/FormSteps';
import { cloneDeep } from 'lodash-es';
import useOfferSorting, { FilterCategory, IFilteringMultiformElement, OfferFilter, OfferFilters } from '@/components/calculator/results/OfferSorting';
import Deal from '@/models/Deal';
import DealsApi from '@/modules/deals/services/DealsApi';
import { LoanPurposeActivity } from '@/models/simulation/LoanPurposeActivity';
import { BankSymbol } from '@/commons/dictionaries/BankSymbol';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { i18nFormatter } from '@/services/StringFormatter';
import { useCalculatorSettings } from '@/components/calculator/useCalculatorSettings';
import Axios from 'axios';
import { useInstance } from '@/composables/useInstance';
import DictionaryApi from '@/commons/services/DictionaryApi';
import { HomeStartProgram, HomeStartProgramInput } from '../schedule/models/HomeStartProgram';
import { IncomeTypesDictionary } from '@/commons/services/models/IncomeTypesDictionary';
import EnvironmentService from '@/env/EnvironmentService';
import { ComparatorEvent, captureComparatorEvent } from '@/plugins/comparatorAnalytics';


const STORE_ID = 'simulation-store';

// a request cancelled by a newer one for the same simulation settles with it - the newer request already carries its changes
let latestSimulationRequest: Nullable<{ simulationId: Nullable<string>; result: Promise<boolean> }> = null;

export type SimulationProductType = ProductType.CASH | ProductType.MORTGAGE

export type SimulationOffersTab = 'available' | 'refused'
export type SimulationResultsTab = 'offers' | 'real-estates'

export type SimulationDemandBasicParameters = Pick<MortgageSimulationDemand & CashSimulationDemand, 'loanAmount' | 'loanPeriodInMonths' | 'loanPeriod' | 'fixedInstallments' | 'additionalRequests' | 'explicitBridgingPeriod' | 'minProvision' | 'loanTypes' | 'brokerCommission' | 'hypothecValue'>

function getUserInputBasicParameters(userInput: SimulationDemandBasicParameters): SimulationDemandBasicParameters {
  return {
    loanAmount: userInput?.loanAmount,
    hypothecValue: userInput?.hypothecValue,
    loanPeriodInMonths: userInput?.loanPeriodInMonths,
    loanPeriod: userInput?.loanPeriod,
    fixedInstallments: userInput?.fixedInstallments,
    explicitBridgingPeriod: userInput?.explicitBridgingPeriod,
    additionalRequests: { ...userInput?.additionalRequests, },
    loanTypes: { ...userInput?.loanTypes, },
    minProvision: userInput?.minProvision,
    brokerCommission: userInput?.brokerCommission,
  };
}

// Matches offers against the active offer filters, grouped by category. Filters hidden by
// their `condition` are skipped — a checkbox the advisor can't see must not silently filter
// (this also stops the RO-only EKO feature checkboxes colliding with the PL "Oferty EKO"
// program filter, since both share the `isEkoTrue` key). PL mortgage program filters combine
// with OR (union); every other category and tenant uses AND. `excludeCategory` drops a whole
// category — used to keep PL program filter counts stable while selecting within that group.
function offersMatchingFilters(
  offers: Offer[],
  categories: IFilteringMultiformElement,
  userFilters: Partial<OfferFilter> | undefined,
  programUsesOr: boolean,
  excludeCategory?: FilterCategory,
): Offer[] {
  const activeByCategory = (Object.entries(categories) as [FilterCategory, OfferFilters[]][])
    .filter(([category]) => category !== excludeCategory)
    .map(([category, categoryFilters]) => ({
      category,
      filters: categoryFilters.filter(f => userFilters?.[f.type] && (!f.condition || f.condition())),
    }))
    .filter(group => group.filters.length > 0);
  return offers.filter(offer => activeByCategory.every(({ category, filters }) =>
    programUsesOr && category === FilterCategory.PROGRAM
      ? filters.some(f => f.filterFunction(offer))
      : filters.every(f => f.filterFunction(offer))));
}

// Bank facets every available-offers list shares: banks the dictionary knows, the advisor's
// bank picks and — unless its own filter is on — special offers.
function offersMatchingBankFacets(offers: Offer[], selectedBanks: BankSymbol[], keepSpecialOffers: boolean): Offer[] {
  const banks = banksDictionary().map(x => x.type);
  return offers
    .filter(x => x.info.bank && banks.includes(x.info.bank))
    .filter(x => selectedBanks.length === 0 || (x.info.bank && selectedBanks.includes(x.info.bank)))
    .filter(x => keepSpecialOffers || !x.features?.isSpecialOffer);
}

interface SimulationStoreState {
  simulation: Nullable<Simulation>;
  userInput: Nullable<SimulationDemand>;
  initialUserInput: Nullable<SimulationDemandBasicParameters>;
  loading: boolean;
  simulationId: Nullable<string>;
  productType: Nullable<SimulationProductType>;
  offersTab: SimulationOffersTab;
  resultsTab: SimulationResultsTab;
  deal: Nullable<Deal>;
  isPrintMode: boolean;
  preventFetch: boolean;
  homeStartProgramAssumptions: Nullable<HomeStartProgram>;
  isFormValid: boolean;
  isHomeStartHidden: boolean;
  stagingDBMode: boolean;
  incomeTypes: IncomeTypesDictionary;
  frozenOfferOrder: Nullable<string[]>;
}

export const useSimulationStore = defineStore(STORE_ID, {
  state: (): SimulationStoreState => ({
    simulation: null,
    userInput: null,
    initialUserInput: null,
    loading: false,
    simulationId: null,
    productType: null,
    offersTab: 'available',
    resultsTab: 'offers',
    deal: null,
    isPrintMode: false,
    preventFetch: false,
    homeStartProgramAssumptions: null,
    isFormValid: false,
    isHomeStartHidden: true,
    stagingDBMode: false,
    incomeTypes: [],
    frozenOfferOrder: null,
  }),
  getters: {
    isAvailableOffersTab: (state): boolean => state.offersTab === 'available',
    isCash: (state) => state.productType === ProductType.CASH,
    isMortgage: (state) => state.productType === ProductType.MORTGAGE,
    /** The offers left by the filters, in the order the active sort option and direction would put them in right now. */
    liveSortedOffers(state): Offer[] {
      const offers = state.simulation?.offers ?? [];
      const { defaultSort, sortItems, offerFilters, } = useOfferSorting();
      const programUsesOr = state.productType === ProductType.MORTGAGE
        && EnvironmentService.Environment.isAppDomain().LENDI_PL;
      return offersMatchingFilters(offers, offerFilters[state.productType!], state.userInput?.offerFilters, programUsesOr)
        .filter(x => !state.userInput?.showOnlySelected ||
          state.userInput.selectedOffers.includes(x.info.id))
        .map((x, index) => ({ ...x, shortName: `${I18NGetter().useSimulationStore.OFFER} #${index + 1}`, }))
        .sort(sortItems[state.productType!].find(x => x.type === state.userInput?.sortBy)?.sortFunction ?? defaultSort);
    },
    /**
     * What the comparator actually lists. Customising an offer must not reshuffle the list under the user,
     * so while `frozenOfferOrder` holds the order captured just before that change, it wins over the live sort.
     * An offer the freeze does not know - one a filter has just let back in - slots in right behind the
     * nearest offer above it in the live sort rather than piling up at the end.
     */
    filteredOffers(state): Offer[] {
      const live: Offer[] = this.liveSortedOffers;
      if (!state.frozenOfferOrder) {
        return live;
      }
      const frozenPositions = new Map<string, number>(state.frozenOfferOrder.map((id, index) => [id, index]));
      let lastFrozenPosition: number = -1;
      return live
        .map((offer) => {
          const frozenPosition: number | undefined = frozenPositions.get(offer.info.id);
          if (frozenPosition !== undefined) {
            lastFrozenPosition = frozenPosition;
          }
          // ties among consecutive unknowns are left to the stable sort, which keeps their live order
          return { offer, position: frozenPosition ?? lastFrozenPosition + 0.5 };
        })
        .sort((a, b) => a.position - b.position)
        .map(({ offer }) => offer);
    },
    allOffers(state): Offer[] {
      // TODO: refactor this
      const isSpecialOfferFilterActive = !!state.userInput?.offerFilters['isSpecialOfferTrue'];
      return offersMatchingBankFacets(this.filteredOffers, state.userInput?.selectedBanks ?? [], isSpecialOfferFilterActive);
    },
    allOffersWithoutDefaultFilter(state): Offer[] {
      return offersMatchingBankFacets(this.filteredOffers, state.userInput?.selectedBanks ?? [], true);
    },
    // Available offers left by the active filters, the `show only selected` narrowing aside —
    // the offers switch counts every bucket regardless of which one is on screen.
    visibleOffers(state): Offer[] {
      const { offerFilters, } = useOfferSorting();
      const programUsesOr = state.productType === ProductType.MORTGAGE
        && EnvironmentService.Environment.isAppDomain().LENDI_PL;
      const isSpecialOfferFilterActive = !!state.userInput?.offerFilters['isSpecialOfferTrue'];
      const offers = offersMatchingFilters(state.simulation?.offers ?? [], offerFilters[state.productType!], state.userInput?.offerFilters, programUsesOr);
      return offersMatchingBankFacets(offers, state.userInput?.selectedBanks ?? [], isSpecialOfferFilterActive);
    },
    // Base for PL mortgage program filter counts: every filter applied EXCEPT the program
    // facet, so each program count reflects its own bucket regardless of the other
    // (OR-combined) program selections. Where program filters use AND it is just the regular base.
    offersForProgramFilterCount(state): Offer[] {
      const programUsesOr = state.productType === ProductType.MORTGAGE
        && EnvironmentService.Environment.isAppDomain().LENDI_PL;
      if (!programUsesOr) {
        return this.allOffersWithoutDefaultFilter;
      }
      const { offerFilters } = useOfferSorting();
      const selectedBanks = state.userInput?.selectedBanks ?? [];
      const offers = offersMatchingFilters(state.simulation?.offers ?? [], offerFilters[state.productType!], state.userInput?.offerFilters, true, FilterCategory.PROGRAM)
        .filter(x => !state.userInput?.showOnlySelected ||
          state.userInput.selectedOffers.includes(x.info.id));
      return offersMatchingBankFacets(offers, selectedBanks, true);
    },
    sortedOffers(state): Offer[] {
      return (this.allOffers as any as Offer[])
        .filter(x => !state.userInput?.pinnedOffers.includes(x.info.id));
    },
    pinnedOffers(state): Offer[] {
      return (this.allOffers as any as Offer[])
        .filter(x => state.userInput?.pinnedOffers.includes(x.info.id));
    },
    /** The visible stack - pinned offers first, then the rest - which the table, the printout and the share modal all follow. */
    displayedOffers(): Offer[] {
      return [...this.pinnedOffers, ...this.sortedOffers];
    },
    /**
     * The frozen order no longer matches what the active sort would produce, so the toolbar offers a re-sort.
     * Compared on the visible stack: a swap between a pinned and an unpinned offer changes nothing on screen.
     */
    isSortOrderStale(state): boolean {
      if (!state.frozenOfferOrder) {
        return false;
      }
      const pinnedIds: string[] = state.userInput?.pinnedOffers ?? [];
      const displayed: Offer[] = this.displayedOffers;
      const visibleIds = new Set<string>(displayed.map(x => x.info.id));
      const liveIds: string[] = this.liveSortedOffers
        .map(x => x.info.id)
        .filter(id => visibleIds.has(id));
      const liveStack: string[] = [
        ...liveIds.filter(id => pinnedIds.includes(id)),
        ...liveIds.filter(id => !pinnedIds.includes(id)),
      ];
      return displayed.some((offer, index) => offer.info.id !== liveStack[index]);
    },
    /**
     * Selected offers in the exact order the comparator presents them: pinned
     * offers first, then the rest in the currently chosen sort. Printouts use
     * this so the printed order matches what the user sees. Offers that are
     * selected but hidden by the active filters still print — appended at the
     * end in the simulation's default order.
     */
    selectedOffersInDisplayOrder(state): Offer[] {
      const selectedIds: string[] = state.userInput?.selectedOffers ?? [];
      const displayed: Offer[] = this.displayedOffers
        .filter(x => selectedIds.includes(x.info.id));
      const displayedIds: string[] = displayed.map(x => x.info.id);
      const hidden = (state.simulation?.offers ?? [])
        .filter(x => selectedIds.includes(x.info.id) && !displayedIds.includes(x.info.id));
      return [...displayed, ...hidden];
    },
    refusedOffers(state): Offer[] {
      const offers = state.simulation?.refusedOffers ?? [];
      const selectedBanks = state.userInput?.refusedOffers.selectedBanks ?? [];
      return offers
        .map((x: Offer, index: number) => ({ ...x, shortName: `${I18NGetter().useSimulationStore.OFFER} #${index + 1}`, }))
        .filter(x => selectedBanks.length === 0 || (x.info.bank && selectedBanks.includes(x.info.bank)));
    },
    offersCount(state): number {
      return state.simulation?.offers.length ?? 0;
    },
    visibleOffersCount(): number {
      return (this.visibleOffers as any as Offer[]).length;
    },
    refusedOffersCount(state): number {
      return state.simulation?.refusedOffers?.length ?? 0;
    },
    visibleOffersCountLabel(state): string {
      const visibleOffersCount: number = this.isAvailableOffersTab
        ? this.sortedOffers.length + this.pinnedOffers.length
        : this.refusedOffersCount;
      const offersCount: number = this.isAvailableOffersTab ? this.offersCount : this.refusedOffersCount;
      return visibleOffersCount === offersCount ? offersCount.toString()
        : `${visibleOffersCount} ${I18NGetter().useSimulationStore.OF} ${offersCount}`;
    },
    isSimulationForDifferentLoanAmounts(state): boolean {
      const loanAmountReplacements = state.userInput?.additionalRequests?.replacements?.loanAmountReplacements;
      return !!(loanAmountReplacements && loanAmountReplacements
        .filter(x => x.newValue !== this.userInput?.loanAmount).length
      );
    },
    isSimulationForDifferentLoanPeriods(state): boolean {
      const loanPeriodReplacements = state.userInput?.additionalRequests?.replacements?.loanPeriodReplacements;
      return !!(loanPeriodReplacements && loanPeriodReplacements
        .filter(x => x.newLoanPeriodInMonths !== this.userInput?.loanPeriodInMonths).length
      );
    },
    selectedOffers(state): Offer[] {
      return this.filteredOffers.filter(offer => {
        return state.userInput?.selectedOffers.includes(offer.info?.id);
      }) ?? [];
    },
    selectedOffersCount(state): number {
      return state.userInput?.selectedOffers.length ?? 0;
    },
    visibleSelectedOffersCount(state): number {
      return (this.visibleOffers as any as Offer[])
        .filter(x => state.userInput?.selectedOffers.includes(x.info.id)).length;
    },
    selectedFiltersCount(state): number {
      if (!this.isAvailableOffersTab) {
        return state.userInput?.refusedOffers.selectedBanks.length ?? 0;
      }
      const selectedFiltersCount: number = Object.values(state.userInput?.offerFilters ?? {}).filter(x => x).length;
      const selectedBanksCount: number = state.userInput?.selectedBanks.length ?? 0;
      return selectedFiltersCount + selectedBanksCount;
    },
    isLoanPurposeActivityBuilding(state): boolean {
      return state.userInput?.loanPurpose.activity === LoanPurposeActivity.BUILDING;
    },
    isLoanPurposeActivityBuying(state): boolean {
      return state.userInput?.loanPurpose.activity === LoanPurposeActivity.BUYING;
    },
    isLoanPurposeActivityBuyingAndRenovating(state): boolean {
      return state.userInput?.loanPurpose.activity === LoanPurposeActivity.BUYING_AND_RENOVATING;
    },
    preventInitialSimulation(state): boolean {
      const { $env, } = useInstance();
      return $env.isAppDomain().NESTO_RO && !state.simulationId && state.productType === ProductType.MORTGAGE;
    },
    ableToCreateSimulation(state): boolean {
      return !!((state.userInput?.loanPurpose.what || state.userInput?.collateral) && state.userInput?.loanPurpose.activity);
    },
  },
  actions: {
    async fetchIncomeTypes() {
      if (this.incomeTypes.length === 0) {
        this.incomeTypes = await DictionaryApi.incomeTypesDictionary();
      }
    },
    async clear() {
      this.frozenOfferOrder = null;
      this.userInput = cloneDeep(useInitialDemands()[this.productType!])!;
      if (!this.userInput?.households?.length) {
        this.userInput.households = [
          new FFHousehold(),
        ];
      }
      this.applyUserGeoDefaults();
      GoToService.simulationForm(this.productType!, FormSteps.CREDIT_PARAMETERS);
    },
    async init(
      productType: SimulationProductType,
      simulationId: Nullable<string> = null,
      deal: Nullable<Deal> = null,
      isPrintMode: boolean = false,
    ) {
      if (productType !== this.productType || simulationId !== this.simulationId) {
        this.simulation = null;
        this.frozenOfferOrder = null;
      }
      this.productType = productType;
      this.simulationId = simulationId;
      this.isPrintMode = isPrintMode;
      if (!this.simulation) {
        if (!this.preventInitialSimulation) {
          this.simulation = await this.fetchSimulation();
        }
        const DemandClasses: Partial<Record<ProductType, typeof SimulationDemand>> = {
          [ProductType.CASH]: CashSimulationDemand,
          [ProductType.MORTGAGE]: MortgageSimulationDemand,
        };
        const Ctor: typeof SimulationDemand = DemandClasses[productType]!;
        const userInput = this.simulation ? new Ctor(this.simulation.userInput)
          : cloneDeep(useInitialDemands()[this.productType!])!;
        if (this.preventInitialSimulation) {
          userInput.loanPurpose.what = null;
          userInput.loanPurpose.activity = null;
        }
        this.setUserInput(userInput);
        // New simulation (none saved) — prefill geo from the expert's profile.
        // A loaded deal (this.simulation) keeps its own saved values.
        if (!this.simulation) {
          this.applyUserGeoDefaults();
        }
        // On a fresh load the results views save their restore point on mount, before the fetch above
        // resolves - "Przywróć oryginalne" would then blank the loan amount. Take it from the loaded input.
        this.saveInitialUserInput();
      }
      if (deal) {
        this.deal = deal;
      }
      this.setSelectedBanksByDefault();
      // if (this.userInput?.homeStartProgramInput) {
      //   await this.getHomeStartProgramAssumptions();
      // }
    },
    setSelectedBanksByDefault() {
      const excludedBanksByProductType: Partial<Record<ProductType, BankSymbol[]>> = {
        [ProductType.CASH]: [BankSymbol.SANTANDER, BankSymbol.CITI],
        [ProductType.MORTGAGE]: [BankSymbol.SBR, BankSymbol.CITI, BankSymbol.SANTANDER],
      };
      const excludedBanks: BankSymbol[] = excludedBanksByProductType[this.productType!] ?? [];
      const banks = banksDictionary().filter(x => x.products![this.productType!]).map(x => x.type);
      this.userInput!.selectedBanks = excludedBanks.length ? banks
        .filter(type => !excludedBanks.includes(type)) : [];
    },
    setUserInput(newUserInput: SimulationDemand): void {
      if (!newUserInput.households?.length) {
        newUserInput.households = [
          new FFHousehold(),
        ];
      }
      this.userInput = newUserInput;
    },
    // For a new simulation, prefill region (land) and county (district) from the
    // logged-in expert's profile. Land and district are treated as a pair (a district
    // belongs to a land), so we override both only when the expert has a land set —
    // otherwise the FFHousehold defaults stay (Warsaw for LENDI_PL).
    applyUserGeoDefaults(): void {
      if (!EnvironmentService.Environment.isAppDomain().LENDI_PL) {
        return;
      }
      const user = Vue.prototype.$user;
      const household = this.userInput?.households?.[0];
      if (!household || user?.landId == null) {
        return;
      }
      household.landId = user.landId;
      household.districtId = user.districtId;
    },
    async fetchSimulation(): Promise<Simulation | null> {
      if (this.simulationId) {
        try {
          this.loading = true;
          const dto = this.isCash
            ? await CashSimulationApi.getSimulationById(this.simulationId)
            : await MortgageSimulationApi.getSimulationById(this.simulationId);
          const response = new Simulation(dto as SimulationDTO<MortgageSimulationDemand>);
          this.simulation = response;
          return response;
        } catch (e) {
          Vue.prototype.errorHandler(e, I18NGetter().useSimulationStore.FETCH_SIMULATION_ERROR_HANDLER);
          const isNotFoundError: boolean = e.response.status === 404;
          if (isNotFoundError) {
            await this.clear();
            Vue.prototype.$snackbarService
              .openWarningSnackbar(I18NGetter().useSimulationStore.NEW_SIMULATION_CREATED);
          }
          return null;
        } finally {
          this.loading = false;
        }
      } else {
        console.error('There is no valid simulationId', this.simulationId);
        return null;
      }
    },
    /** `pinEditedOffer` defaults to the long-standing behaviour - the offer edit modal is what opts out of it. */
    async createSimulation(loanId: string, pinEditedOffer: boolean = true): Promise<boolean> {
      const previousFrozenOfferOrder: Nullable<string[]> = this.frozenOfferOrder;
      try {
        this.freezeOfferOrder();
        if (pinEditedOffer && !this.userInput?.pinnedOffers.includes(loanId)) {
          await this.pinOffer(loanId);
        }
        const isSaved: boolean = await this.createOrUpdateSimulation();
        if (!isSaved) {
          this.frozenOfferOrder = previousFrozenOfferOrder;
        }
        return isSaved;
      } catch (e) {
        this.frozenOfferOrder = previousFrozenOfferOrder;
        Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useSimulationStore
          .CREATE_SIMULATION_IF_AVAILABLE_CATCH_ERROR_SNACKBAR);
        console.error('offer not available');
        return false;
      }
    },
    async createSimulationIfAvailable(loanId: string, pinEditedOffer: boolean = true): Promise<boolean> {
      try {
        // A failed check counts as an unavailable loan, so it gets the same snackbar as a negative answer.
        const isAvailable: boolean = await (this.isCash
          ? CashSimulationApi.isLoanAvailable(loanId, this.userInput! as CashIsLoanAvailableRequest)
          : MortgageSimulationApi.isLoanAvailable(loanId, this.userInput! as MortgageIsLoanAvailableRequest))
          .then(response => response.isAvailable)
          .catch(() => false);
        if (!isAvailable) {
          Vue.prototype.$snackbarService.openErrorSnackbar(
            I18NGetter().useSimulationStore.CREATE_SIMULATION_IF_AVAILABLE_TRY_ERROR_SNACKBAR
          );
          return false;
        }
        return await this.createSimulation(loanId, pinEditedOffer);
      } catch (e) {
        Vue.prototype.errorHandler(
          e,
          I18NGetter().useSimulationStore.CREATE_SIMULATION_IF_AVAILABLE_CATCH_ERROR_SNACKBAR
        );
        return false;
      }
    },
    cleanSelectedOffersMovedToRefused() {
      if (!this.selectedOffersCount) {
        return;
      }
      const allOffersIds: string[] = (this.simulation?.offers ?? []).map(x => x.info.id);
      const refusedOffersIds: string[] = (this.simulation?.refusedOffers ?? []).map(x => x.info.id);
      const selectedOffersMovedToRefusedCount: number = this.userInput?.selectedOffers
        .filter(x => refusedOffersIds.includes(x)).length ?? 0;
      if (selectedOffersMovedToRefusedCount) {
        this.userInput!.selectedOffers = this.userInput!.selectedOffers.filter(x => allOffersIds.includes(x));
        if (this.userInput!.selectedOffers.length > 0) {
          Vue.prototype.$snackbarService.openWarningSnackbar(
            `${selectedOffersMovedToRefusedCount > 1 ? i18nFormatter(I18NGetter().useSimulationStore.NEW_TRANSLATION_FEW_OFFERS_DESELECTED, { offersCount: selectedOffersMovedToRefusedCount, }) : I18NGetter().useSimulationStore.NEW_TRANSLATION_ONE_OFFER_DESELECTED} `
          );
        } else {
          this.userInput!.showOnlySelected = false;
          Vue.prototype.$snackbarService.openWarningSnackbar(
            I18NGetter().useSimulationStore.ALL_OFFERS_MOVED_TO_REFUSED_INFO
          );
        }
      }
    },
    createOrUpdateSimulation(): Promise<boolean> {
      latestSimulationRequest = { simulationId: this.simulationId, result: this.requestSimulation(), };
      return latestSimulationRequest.result;
    },
    async requestSimulation(): Promise<boolean> {
      const userInputWithHomeStartProgramInput = this.userInput
      const userInputWithoutHomeStartProgramInput = removePropertyFromObject(this.userInput!, ['homeStartProgramInput',]);
      const userInput = this.isHomeStartHidden ? userInputWithoutHomeStartProgramInput : userInputWithHomeStartProgramInput;
      const simulationId = this.simulationId;
      const productType = this.productType;
      const ableToPerformSimulation: boolean =
        !!((userInput?.loanAmount || userInput?.hypothecValue));

      if (productType && userInput && ableToPerformSimulation) {
        if (productType === ProductType.MORTGAGE && !userInput.loanPeriodInMonths) {
          userInput.loanPeriodInMonths = userInput.loanPeriod! * 12;
        }
        try {
          if (simulationId) {
            // TODO: unify simulation update logic
            if (productType === ProductType.MORTGAGE) {
              this.loading = true;
              const response = await MortgageSimulationApi.updatePersistentSimulationById(
                simulationId,
                userInput as MortgageUpdatePersistentSimulationByIdRequest,
              );
              this.simulation = new Simulation(response as SimulationDTO<MortgageSimulationDemand>);
            } else {
              this.loading = true;
              await CashSimulationApi.updateSimulationById(simulationId, userInput as CashUpdateSimulationByIdRequest);
              setTimeout(async () => {
                try {
                  this.loading = true
                  await this.fetchSimulation();
                } catch (e) {
                  Vue.prototype.errorHandler(e, I18NGetter().useSimulationStore.CREATE_UPDATE_SIMULATION_CATCH_ERROR);
                } finally {
                  this.loading = false;
                }
              }, 1000);
            }
          } else {
            if (this.ableToCreateSimulation) {
              const simulationPostDTO = productType === ProductType.CASH
                ? await CashSimulationApi.createSimulation(userInput as CashCreateSimulationRequest)
                : await MortgageSimulationApi.createSimulation(userInput as MortgageCreateSimulationRequest);
              this.simulationId = simulationPostDTO.id;
              await this.fetchSimulation();
            }
          }
          this.cleanSelectedOffersMovedToRefused();
          if (this.deal && !this.deal.simulationId) {
            this.assignSimulationToDeal();
          }
          // if (this.isFormValid && !this.isHomeStartHidden) this.getHomeStartProgramAssumptions();
          this.loading = false;
          return true;
        } catch (e) {
          if (!Axios.isCancel(e)) {
            console.error('error', e)
            this.loading = false;
            Vue.prototype.errorHandler(e, I18NGetter().useSimulationStore.CREATE_UPDATE_SIMULATION_CATCH_ERROR);
          } else {
            console.error(e, 'Zapytanie anulowane, czekaj na kolejną odpowiedź');
            return latestSimulationRequest?.simulationId === simulationId ? latestSimulationRequest.result : false;
          }
          return false;
        }
      } else {
        console.error('There is no valid productType or userInput');
        return false;
      }
    },
    saveInitialUserInput() {
      this.initialUserInput = getUserInputBasicParameters(
        this.userInput as unknown as SimulationDemandBasicParameters
      );
    },
    restoreInitialUserInput() {
      this.userInput = {
        ...this.userInput,
        ...getUserInputBasicParameters(this.initialUserInput!),
      } as SimulationDemand;
    },
    async saveSimulation() {
      if (this.simulationId && this.productType && this.userInput) {
        await (this.isCash
          ? CashSimulationApi.updateSimulationById(this.simulationId, this.userInput as CashUpdateSimulationByIdRequest)
          : MortgageSimulationApi.updateSimulationById(this.simulationId, this.userInput as MortgageUpdateSimulationByIdRequest));
      } else {
        throw new Error(I18NGetter().useSimulationStore.SAVE_SIMULATION_ERROR);
      }
    },
    debouncedUpdateSimulation() {
      const { INPUT_DEBOUNCE_TIME_IN_MILLISECONDS, } = useCalculatorSettings();
      return debouncedAtInput(async () => {
        await this.createOrUpdateSimulation();
      }, INPUT_DEBOUNCE_TIME_IN_MILLISECONDS);
    },
    unselectAllBanks() {
      if (this.isAvailableOffersTab) {
        this.userInput!.selectedBanks = [];
      } else {
        this.userInput!.refusedOffers.selectedBanks = [];
      }
    },
    unselectAllFilters() {
      const filterKeys = Object.keys(this.userInput!.offerFilters);
      filterKeys.forEach((key: string) => {
        this.userInput!.offerFilters[key] = false;
      });
    },
    async removeCustomLoanAmounts() {
      if (this.userInput?.additionalRequests.replacements?.loanAmountReplacements) {
        this.userInput.additionalRequests.replacements.loanAmountReplacements = [];
      }
      await this.createOrUpdateSimulation();
    },
    async removeCustomLoanPeriods() {
      if (this.userInput?.additionalRequests.replacements?.loanPeriodReplacements) {
        this.userInput.additionalRequests.replacements.loanPeriodReplacements = [];
      }
      await this.createOrUpdateSimulation();
    },
    async removeCustomValues() {
      if (this.userInput?.additionalRequests.replacements?.loanPeriodReplacements) {
        this.userInput.additionalRequests.replacements.loanPeriodReplacements = [];
      }
      if (this.userInput?.additionalRequests.replacements?.loanAmountReplacements) {
        this.userInput.additionalRequests.replacements.loanAmountReplacements = [];
      }
      await this.createOrUpdateSimulation();
    },
    // Comparator V2.5 product analytics for offer interactions. Single choke
    // point for all surfaces (table / list / details / 360). No-op outside
    // V2.5 (mortgage + LENDI_PL) and PII-safe — only the allowlisted props in
    // comparatorAnalytics.ts ever leave the browser.
    trackComparatorOfferInteraction(event: ComparatorEvent, id: string) {
      if (!this.isMortgage || !EnvironmentService.Environment.isAppDomain().LENDI_PL) {
        return;
      }
      const offer = this.simulation?.offers?.find(item => item.info.id === id);
      const position = this.allOffers.findIndex(item => item.info.id === id);
      captureComparatorEvent(event, {
        simulationId: this.simulationId ?? null,
        productType: this.productType ? String(this.productType) : null,
        loanPurpose: this.userInput?.loanPurpose?.activity ?? null,
        bankSymbol: offer?.info?.bank ?? null,
        position: position >= 0 ? position + 1 : null,
        offersCount: this.offersCount,
        selectedOffersCount: this.userInput?.selectedOffers?.length ?? 0,
        pinnedOffersCount: this.userInput?.pinnedOffers?.length ?? 0,
      });
    },
    async pinOffer(id: string) {
      this.userInput!.pinnedOffers.push(id);
      await (this.isCash ? CashSimulationApi : MortgageSimulationApi).pinOffer(this.simulationId!, id);
      this.trackComparatorOfferInteraction(ComparatorEvent.OFFER_PINNED, id);
    },
    async unpinOffer(id: string) {
      this.userInput!.pinnedOffers = this.userInput!.pinnedOffers.filter(item => item !== id);
      await (this.isCash ? CashSimulationApi : MortgageSimulationApi).unpinOffer(this.simulationId!, id);
      this.trackComparatorOfferInteraction(ComparatorEvent.OFFER_UNPINNED, id);
    },
    async selectOffer(id: string) {
      this.userInput!.selectedOffers.push(id);
      await (this.isCash ? CashSimulationApi : MortgageSimulationApi).selectOffer(this.simulationId!, id);
      this.trackComparatorOfferInteraction(ComparatorEvent.OFFER_SELECTED, id);
    },
    async unselectOffer(id: string) {
      this.userInput!.selectedOffers = this.userInput!.selectedOffers.filter(item => item !== id);
      if (this.userInput!.showOnlySelected && !this.userInput!.selectedOffers.length) {
        this.userInput!.showOnlySelected = false;
      }
      await (this.isCash ? CashSimulationApi : MortgageSimulationApi).unselectOffer(this.simulationId!, id);
      this.trackComparatorOfferInteraction(ComparatorEvent.OFFER_UNSELECTED, id);
    },
    async selectAllOffers() {
      const { userInput, productType, simulationId } = this;
      if (!userInput || !productType || !simulationId) {
        throw new Error(I18NGetter().useSimulationStore.SIMULATION_NOT_INITIALIZED_ERROR);
      }

      const ids: string[] = this.allOffers
        .map(offer => offer.info.id)
        .filter(id => !userInput.selectedOffers.includes(id));
      const results = await Promise.allSettled(
        ids.map(id => (productType === ProductType.CASH ? CashSimulationApi : MortgageSimulationApi).selectOffer(simulationId, id))
      );
      userInput.selectedOffers.push(...ids.filter((_, index) => results[index].status === 'fulfilled'));
      const rejected = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (rejected) {
        throw rejected.reason;
      }
    },
    async unselectedAllOffers() {
      const { userInput, productType, simulationId } = this;
      if (!userInput || !productType || !simulationId) {
        throw new Error(I18NGetter().useSimulationStore.SIMULATION_NOT_INITIALIZED_ERROR);
      }

      const ids: string[] = [...userInput.selectedOffers];
      const results = await Promise.allSettled(
        ids.map(id => (productType === ProductType.CASH ? CashSimulationApi : MortgageSimulationApi).unselectOffer(simulationId, id))
      );
      const unselectedIds: string[] = ids.filter((_, index) => results[index].status === 'fulfilled');
      userInput.selectedOffers = userInput.selectedOffers.filter(id => !unselectedIds.includes(id));
      if (!userInput.selectedOffers.length) {
        userInput.showOnlySelected = false;
      }
      const rejected = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (rejected) {
        throw rejected.reason;
      }
    },
    async duplicateOffer(id: string) {
      this.userInput!.additionalRequests = (this.isCash
        ? await CashSimulationApi.duplicateLoan(id, this.userInput!.additionalRequests! as CashDuplicateLoanRequest)
        : await MortgageSimulationApi.duplicateLoan(id, this.userInput!.additionalRequests! as MortgageDuplicateLoanRequest)) as AdditionalRequests;
      const variants: LoanVariant[] = this.userInput!.additionalRequests
        .loanVariants!
        .filter(x => x.originLoanId === id);
      const duplicateOfferId: string = variants[variants.length - 1]?.variantLoanId;
      if (duplicateOfferId) {
        this.userInput!.pinnedOffers.push(duplicateOfferId);
        const isOfferSelected: boolean = this.userInput!.selectedOffers.includes(id);
        if (isOfferSelected) {
          this.userInput!.selectedOffers.push(duplicateOfferId);
        }
        await this.createOrUpdateSimulation();
      }
    },
    async removeDuplication(id: string) {
      this.userInput!.additionalRequests = (this.isCash
        ? await CashSimulationApi.removeDuplicate(id, this.userInput!.additionalRequests! as CashRemoveDuplicateRequest)
        : await MortgageSimulationApi.removeDuplicate(id, this.userInput!.additionalRequests! as MortgageRemoveDuplicateRequest)) as AdditionalRequests;
      this.simulation!.offers = this.simulation!.offers.filter(x => x.info.id !== id);
      this.userInput!.selectedOffers = this.userInput!.selectedOffers.filter(x => x !== id);
      if (this.userInput!.showOnlySelected && !this.userInput!.selectedOffers.length) {
        this.userInput!.showOnlySelected = false;
      }
      await this.createOrUpdateSimulation();
    },
    async deleteSimulationFromDeal() {
      this.simulationId = null;
      this.deal = null;
      await this.createOrUpdateSimulation();
    },
    async setDeal(dealId: number) {
      try {
        this.deal = await DealsApi.getDeal(dealId);
      } catch (e) {
        Vue.prototype.errorHandler(e, I18NGetter().useSimulationStore.FETCH_DEAL_ERROR);
      }
    },
    async assignSimulationToDeal() {
      try {
        this.deal = await DealsApi.updateDeal(
          {
            ...this.deal as Deal,
            simulationId: this.simulationId,
            loanAmount: this.userInput?.loanAmount || this.deal?.loanAmount,
          });
      } catch (e) {
        Vue.prototype.errorHandler(e, I18NGetter().useSimulationStore.UPDATE_DEAL_ERROR);
      }
    },
    toggleHideOfferName() {
      this.userInput!.hideOfferName = !this.userInput!.hideOfferName;
    },
    async getHomeStartProgramAssumptions() {
      const loanAmount = this.userInput?.loanAmount;
      const data: HomeStartProgramInput & { loanAmount: number } = {
        ...this.userInput?.homeStartProgramInput,
        loanAmount: loanAmount!,
      } as HomeStartProgramInput & { loanAmount: number };
      try {
        this.homeStartProgramAssumptions = await MortgageSimulationApi.getHomeStartAssumptions(
          data! as MortgageGetHomeStartAssumptionsRequest,
        ) as Nullable<HomeStartProgram>;
      } catch (e) {
        Vue.prototype.errorHandler(e, 'Błąd pobierania danych programu Mieszkanie na start');
      }
    },
    setIsFormValid(isValid: boolean) {
      this.isFormValid = isValid;
    },
    setIsHomeStartFormHidden(isHidden: boolean) {
      this.isHomeStartHidden = isHidden;
    },
    setReplacement<T extends ReplacementKind>(type: T, index: number, replacement: InferredReplacement<T>): void {
      this.userInput!.additionalRequests!.replacements![type] = this.userInput!
        .additionalRequests.replacements![type].map(
          (replacementOffer, i: number) => i === index ? { ...replacementOffer, ...replacement, } : replacementOffer) as (InferredReplacement<typeof type>)[]
    },
    setReplacements(newReplacements: Replacements) {
      if (!this.userInput) return;
      this.userInput.additionalRequests.replacements = newReplacements;
    },
    /**
     * Pins the order on screen, so recalculating an offer cannot move the list under the user.
     * V2.5 mortgage only - that is the surface carrying the re-sort button which lifts the freeze again.
     */
    freezeOfferOrder() {
      if (!this.isMortgage || !EnvironmentService.Environment.isAppDomain().LENDI_PL) {
        return;
      }
      this.frozenOfferOrder = this.displayedOffers.map(x => x.info.id);
    },
    /** Lifts the freeze, so the list falls back to the active sort option and direction. */
    resortOffers() {
      this.frozenOfferOrder = null;
    },
  },
});
